import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { FilterItemType, MetricFilterOperator } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';

import Serviced from 'webapp/models-custom/serviced';

export type OnChangeProp = keyof DSSetup.MetricSetupFilterItemData;
export type OnChangePropValue =
	DSSetup.MetricSetupFilterItemData[keyof DSSetup.MetricSetupFilterItemData];

// used to inform FilterGroup about changes on the item
export type OnChangeFn = (_prop: OnChangeProp, _value: OnChangePropValue) => void;

export default class FilterItem extends Serviced {
	@service('constants') declare constantsService: Service.Constants;

	@tracked private _type: FilterItemType;
	@tracked private _field: string;
	@tracked private _operator: MetricFilterOperator;
	@tracked private _values: string[];
	@tracked private _vendorOperator?: string;
	@tracked private _source?: Record<string, unknown>;

	private onChange: OnChangeFn | null = null;

	constructor(props: DSSetup.MetricSetupFilterItemData, onChange?: OnChangeFn) {
		super();

		this._type = props.type;
		this._field = props.field;
		this._operator = props.operator;
		this._values = props.values;
		this._vendorOperator = props.vendorOperator;
		this._source = props.source;

		if (onChange) {
			this.onChange = onChange;
		}
	}

	get type(): FilterItemType {
		return this._type;
	}

	set type(value: FilterItemType) {
		if (value === this._type) {
			return;
		}

		this._type = value;
		this.notifyChange('type', value);
	}

	get field(): string {
		return this._field;
	}

	set field(value: string) {
		if (value === this._field) {
			return;
		}

		this._field = value;
		// vendor details belong to the previous field
		this._vendorOperator = undefined;
		this._source = undefined;
		this.notifyChange('field', value);
	}

	get operator(): MetricFilterOperator {
		return this._operator;
	}

	set operator(value: MetricFilterOperator) {
		if (value === this._operator) {
			return;
		}

		this._operator = value;
		// vendor operator belongs to the previous operator
		this._vendorOperator = undefined;
		this.notifyChange('operator', value);
	}

	get values(): string[] {
		return this._values;
	}

	set values(value: string[]) {
		if (equal(value, this._values)) {
			return;
		}

		this._values = value;
		this.notifyChange('values', value);
	}

	get vendorOperator(): string | undefined {
		return this._vendorOperator;
	}

	get source(): Record<string, unknown> | undefined {
		return this._source;
	}

	get isTypeDimension() {
		return this.type === FilterItemType.DIMENSION;
	}

	get isTypeUnit() {
		return this.type === FilterItemType.UNIT;
	}

	get isAdvancedFilter(): boolean {
		return this.operator !== MetricFilterOperator.ANY_OF;
	}

	@cached
	get operatorText() {
		const text =
			this.constantsService.metricFilterOperators.find((i) => i.value === this.operator)
				?.label || this.operator;
		return text.toLowerCase();
	}

	@cached
	get filterItemData(): DSSetup.MetricSetupFilterItemData {
		const data: DSSetup.MetricSetupFilterItemData = {
			type: this.type,
			field: this.field,
			operator: this.operator,
			values: this.values,
		};
		if (this.vendorOperator) {
			data.vendorOperator = this.vendorOperator;
		}
		if (this.source) {
			data.source = this.source;
		}
		return data;
	}

	checkIfIsBasicFilterForDimension(dimension: string): boolean {
		return this.field === dimension && this.operator === MetricFilterOperator.ANY_OF;
	}

	isEqual(other: FilterItem): boolean {
		return (
			this.type === other.type &&
			this.field === other.field &&
			this.operator === other.operator &&
			equal(this.values, other.values) &&
			this.vendorOperator === other.vendorOperator &&
			equal(this.source, other.source)
		);
	}

	setFromFilterItemData(data: DSSetup.MetricSetupFilterItemData): void {
		this._type = data.type;
		this._field = data.field;
		this._operator = data.operator;
		this._values = data.values;
		this._vendorOperator = data.vendorOperator;
		this._source = data.source;

		// now trigger all at once
		this.notifyChange('type', data.type);
		this.notifyChange('field', data.field);
		this.notifyChange('operator', data.operator);
		this.notifyChange('values', data.values);
	}

	// --------- onChange notifier ----------

	protected notifyChange(prop: OnChangeProp, value: OnChangePropValue): void {
		if (this.onChange) {
			this.onChange(prop, value);
		}
	}
}
